import React, { FC } from 'react';
import { Image, View } from 'react-native';
import { useQueryClient } from 'react-query';
import axios from 'axios';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';

import { assets } from '@assets';
import { apiConfig, ENVIRONMENT, loadingPageTestIDs, onboardingTestIDs, Theme } from '@constants';
import { useContextNodeHandles } from '@hooks';
import { navigationService } from '@services';
import { environmentsState, userAuthState, userSettingsState } from '@store';

import { getDeviceKey } from '../../../helpers/KeyUtils';
import { platformName } from '../../../helpers/Platform';
import { clearStorage, setStorageItem } from '../../../helpers/storage';
import { getToken } from '../../../hooks/api/useGetAdobeTVEUserAuth';
import { FocusableSwitch } from '../../focusable/FocusableSwitch/FocusableSwitch';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCText } from '../../primitives/TWCText/TWCText';
import { segmentEventTypes } from '../../segment/segmentEventTypes';
import { segmentTrackEvent } from '../../segment/SegmentTrackEvent';
import { DebugItemType } from '../DebugItems';
import { styles } from '../styles';

export const DebugMainMenuItems: FC<DebugMainMenuItemsProps> = ({ items }) => {
  const debugNodeHandles = useContextNodeHandles(items.map(item => item.id));
  const [{ environments, overrideEnv }, setEnvironments] = useRecoilState(environmentsState);
  const userSettings = useRecoilValue(userSettingsState);
  const resetUserSettings = useResetRecoilState(userSettingsState);
  const resetUserAuthState = useResetRecoilState(userAuthState);
  const deviceID = getDeviceKey();
  const { deviceInfoString, isCableSubscriber } = userSettings;
  const queryClient = useQueryClient();

  const envIndex = items.findIndex(element => element.id === 'enviroment');

  const finishSigningOut = () => {
    if (overrideEnv) {
      clearStorage(false);
      const newData = {
        overrideEnv,
        environments: {
          prod: !environments?.prod,
          debug: !environments?.debug,
        },
      };

      if (isCableSubscriber) {
        segmentTrackEvent(segmentEventTypes.LOGOUT, userSettings);
      }

      setEnvironments({
        ...newData,
      });
      setTimeout(() => {
        resetUserAuthState();
        resetUserSettings();
        setStorageItem(ENVIRONMENT, JSON.stringify(newData));
        queryClient.clear();

        navigationService.replace(loadingPageTestIDs.loadingPage);
      }, 2000);
    }
  };

  const signOut = async (retry = false) => {
    if (isCableSubscriber) {
      const token = await getToken(retry);

      return await axios
        .delete(`${apiConfig.ADOBE_TVE_LOGOUT_URL}?deviceId=${deviceID}&deviceType=${platformName()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Device-Info': deviceInfoString,
          },
        })
        .then(() => {
          finishSigningOut();
        })
        .catch(error => {
          // if we get a 401, the token has expired, so try again, which will force a new token in getToken
          if (error.response.status === 401) {
            return retry ? { error: 'access error' } : signOut(true);
          } else {
            // we got another kind of error, so send the user to the error page
            // this error page is designed for the onboarding flow. we need to talk to product/design about what we should actually do here
            // perhaps an inline message on the current page?
            navigationService.navigate(onboardingTestIDs.pages.errorPage);
          }
        });
    } else {
      finishSigningOut();
    }
  };

  if (envIndex >= 0) {
    // Update env debug item with the onPress functionality and value
    items[envIndex] = {
      ...items[envIndex],
      onPress: signOut,
      value: environments?.debug,
    };
  }

  return (
    <View style={Theme.styles.center}>
      {items.map((item, index) => {
        const { id, pageID, title, type, activeText, inActiveText, onPress, value } = item;
        const numberOfItems = items.length;
        const nextFocusDownHandle = index < numberOfItems - 1 ? debugNodeHandles[index + 1] : undefined;
        const nextFocusUpHandle = index > 0 ? debugNodeHandles[index - 1] : undefined;

        switch (type) {
          case 'button':
            return (
              <TWCButton
                key={index}
                nextFocusDown={nextFocusDownHandle}
                nextFocusLeft={null}
                nextFocusRight={null}
                nextFocusUp={nextFocusUpHandle}
                style={styles.button}
                testID={id}
                title={title}
                onPress={() =>
                  navigationService.navigate(pageID, {
                    debugItem: { pageID, id, title },
                  })
                }
              />
            );
          case 'switch':
            return (
              <View key={index} style={styles.switchWrapper}>
                <TWCText testID={'textProd'} variant={'regularText36'}>
                  {title}
                </TWCText>
                <FocusableSwitch
                  activeText={activeText}
                  backgroundActive={Theme.switch.activeColor}
                  backgroundInactive={Theme.withOpacity(Theme.white, 0.15)}
                  barHeight={Theme.px(80)}
                  changeValueImmediately
                  circleBorderWidth={0}
                  circleSize={Theme.px(54)}
                  colorFocused={Theme.switch.focusColor}
                  inActiveText={inActiveText}
                  nativeID={id}
                  nextFocusDown={nextFocusDownHandle}
                  nextFocusLeft={undefined}
                  nextFocusRight={undefined}
                  nextFocusUp={nextFocusUpHandle}
                  renderInsideCircle={() =>
                    value ? <Image source={assets.toggleOn} style={styles.checkIcon} /> : <></>
                  }
                  shouldUseContextReference
                  switchLeftPx={2}
                  switchRightPx={2}
                  switchWidthMultiplier={3.15}
                  testID={id}
                  value={value}
                  onValueChange={onPress}
                />
              </View>
            );
          default:
            return null;
        }
      })}
    </View>
  );
};

type DebugMainMenuItemsProps = {
  items: DebugItemType[];
};
